import { KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  MdArrowBack,
  MdClose,
  MdOutlineDeleteSweep,
  MdOutlineFlipCameraIos,
  MdOutlinePhotoCamera,
} from 'react-icons/md';
import { Environments, FormOption } from 'types';
import barcodeSound from 'static/sound/barcode.wav';
import { BarcodeScanner, FacingMode } from 'components/barcode-scanner';
import { Button } from 'components/button';
import { Text } from 'components/text';
import { ENV, FontWeight, KEYBOARD } from 'commons';
import * as S from './styles';
import { useSelect } from '../../hooks';

function isScannedCodeValid({
  scannedCode,
  scannedCodeList,
}: {
  scannedCode: string;
  scannedCodeList: FormOption[];
}) {
  if (scannedCodeList.some((tag) => tag.value === scannedCode)) return false;

  switch (ENV) {
    case Environments.Prod:
      const regexProd = /^[A-Z]\d{8,10}-\d{1,2}$/;

      return regexProd.test(scannedCode);

    case Environments.Preprod:
    case Environments.Hotfix:
      const regexPreprod = /^[A-Z]PP\d{8,10}-\d{1,2}$/;

      return regexPreprod.test(scannedCode);

    default:
      const regexInteg = /^[A-Z]IN\d{2}-\d{8,10}-\d{1,2}$/;

      return regexInteg.test(scannedCode);
  }
}

const audio = new Audio(barcodeSound);

interface Props {
  hideButton?: boolean;
}

export function ScanCodesButton({ hideButton = false }: Props) {
  const { tagList, setTagList, setShowOptions } = useSelect();

  const [showCamera, setShowCamera] = useState(false);
  const [facingMode, setFacingMode] = useState<FacingMode>(
    FacingMode.Environment
  );

  useEffect(() => {
    function handleReturnState(event: Event) {
      event.stopPropagation();
      event.preventDefault();

      setShowCamera(false);
    }

    if (showCamera) {
      window.history.pushState('', '');

      window.addEventListener('popstate', handleReturnState);
    } else {
      window.removeEventListener('popstate', handleReturnState);
    }

    return () => {
      window.removeEventListener('popstate', handleReturnState);
    };
  }, [showCamera]);

  if (hideButton) return null;

  return (
    <div onClick={(event: MouseEvent) => event.stopPropagation()}>
      <S.Main
        onClick={() => {
          setShowOptions(false);
          setShowCamera(true);
        }}
        onKeyDown={(event: KeyboardEvent) => {
          event.stopPropagation();

          const { key } = event;

          if (key === KEYBOARD.enter) {
            setShowOptions(false);
            setShowCamera(true);
          }
        }}
        tabIndex={0}
        aria-label="button"
      >
        <MdOutlinePhotoCamera />
      </S.Main>

      {showCamera &&
        createPortal(
          <S.FullScreenScanner>
            <S.Header>
              <Button variant="transparent" onClick={() => setTagList([])}>
                <S.Icon>
                  <MdOutlineDeleteSweep />
                </S.Icon>

                <Text>Limpar lista</Text>
              </Button>
            </S.Header>

            <S.CameraWrapper>
              <BarcodeScanner
                facingMode={facingMode}
                onResult={(result) => {
                  if (result && result.getText()) {
                    const scannedCode = result.getText().trim();

                    setTagList((cur) => {
                      if (
                        isScannedCodeValid({
                          scannedCode,
                          scannedCodeList: cur,
                        })
                      ) {
                        audio.play();

                        return [
                          ...cur,
                          { label: scannedCode, value: scannedCode },
                        ];
                      } else {
                        return cur;
                      }
                    });
                  }
                }}
              />

              <S.ChangeCameraButton
                type="button"
                onClick={() =>
                  setFacingMode((cur) => {
                    return cur === FacingMode.Environment
                      ? FacingMode.User
                      : FacingMode.Environment;
                  })
                }
              >
                <S.Icon>
                  <MdOutlineFlipCameraIos />
                </S.Icon>
              </S.ChangeCameraButton>
            </S.CameraWrapper>

            <S.CodesWrapper>
              {tagList.map((tag) => (
                <S.Tag key={tag.value}>
                  <Text weight={FontWeight.bold} className="tag-text">
                    {tag.value}
                  </Text>

                  <S.RemoveTagButton
                    onClick={() =>
                      setTagList((cur) =>
                        cur.filter((curTag) => curTag.value !== tag.value)
                      )
                    }
                  >
                    <MdClose size={16} />
                  </S.RemoveTagButton>
                </S.Tag>
              ))}
            </S.CodesWrapper>

            <Button onClick={() => setShowCamera(false)} full>
              {tagList.length ? (
                <Text>Finalizar</Text>
              ) : (
                <>
                  <S.Icon>
                    <MdArrowBack />
                  </S.Icon>

                  <Text>Voltar</Text>
                </>
              )}
            </Button>
          </S.FullScreenScanner>,
          document.body
        )}
    </div>
  );
}
